import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import {
  requestSendInvoiceReminder,
  requestMarkInvoiceAsOutstanding,
  requestMarkInvoiceAsPaid,
  requestRefundInvoice,
  requestUpdateInvoice,
  requestCreateInvoice,
} from '../../../actions/invoicesActions'
import { deserialize } from '../../../utility/serialization'

// "No Package" produces a payload with no charge line item — there's nothing to bill.
const hasChargeLineItem = (lineItems) => (lineItems || []).some(item => item.lineType !== 'discount')

// Invoice lifecycle actions (R3). Each hits a coach invoice endpoint, then refreshes the drawer.
// Extracted from EnrollmentDetailDrawer to keep that component under the max-lines limit.
const useInvoiceActions = ({ enrollment, setActiveModal, setActionLoading, onActionComplete, loadEnrollment }) => {
  const { t } = useTranslation()
  const [showMarkPaidSuccess, setShowMarkPaidSuccess] = useState(false)
  const [markPaidEmailSent, setMarkPaidEmailSent] = useState(false)
  const [showReminderSuccess, setShowReminderSuccess] = useState(false)

  const runInvoiceAction = (request, successMessage, onSuccess) => {
    setActionLoading(true)
    request()
      .then(() => {
        setActiveModal(null)
        onActionComplete?.()
        loadEnrollment()
        if (onSuccess) {
          onSuccess()
        } else {
          toast.success(successMessage, { position: toast.POSITION.TOP_RIGHT })
        }
      })
      .catch(() => toast.error(t('enrollmentsBilling.actions.somethingWentWrong'), { position: toast.POSITION.TOP_RIGHT }))
      .finally(() => setActionLoading(false))
  }

  const invoiceId = enrollment?.invoice?.id
  const invoiceHandlers = {
    onSendReminder: () => runInvoiceAction(
      () => requestSendInvoiceReminder(invoiceId),
      t('enrollmentsBilling.actions.reminderSent'),
      () => setShowReminderSuccess(true)
    ),
    onMarkOutstanding: () => setActiveModal('invoiceOutstanding'),
    onMarkPaid: () => setActiveModal('invoicePaid'),
    onRefund: () => setActiveModal('invoiceRefund'),
    onEdit: () => setActiveModal('invoiceEdit'),
  }
  const handleMarkPaid = params => runInvoiceAction(
    () => requestMarkInvoiceAsPaid(invoiceId, params),
    t('enrollmentsBilling.actions.invoiceMarkedPaid'),
    () => {
      setMarkPaidEmailSent(!!params.sendEmail)
      setShowMarkPaidSuccess(true)
    }
  )
  const handleRefund = params => runInvoiceAction(() => requestRefundInvoice(invoiceId, params), t('enrollmentsBilling.actions.refundIssued'))
  const handleMarkOutstanding = () => runInvoiceAction(
    () => requestMarkInvoiceAsOutstanding(invoiceId),
    t('enrollmentsBilling.actions.invoiceMarkedOutstanding')
  )

  // Attach a package to an invoice-less enrollment: create the invoice, then (if Paid) chain a
  // mark-as-paid against the freshly-created invoice id. "No Package" → nothing to bill; just close.
  const handleCreateInvoice = ({ markAsPaid, sendEmail, ...createParams }) => {
    if (!hasChargeLineItem(createParams.lineItems)) {
      setActiveModal(null)
      return
    }

    runInvoiceAction(
      () => requestCreateInvoice(enrollment.id, createParams).then((response) => {
        if (!markAsPaid) { return null }

        const newInvoiceId = deserialize(response.data).invoice.id
        return requestMarkInvoiceAsPaid(newInvoiceId, {
          paymentMethod: createParams.paymentMethod,
          offlinePaymentDescription: createParams.offlinePaymentDescription,
          sendEmail,
        })
      }),
      t('enrollmentsBilling.actions.invoiceCreated')
    )
  }

  // Edit saves the invoice changes, then (if the toggle is set to Paid) chains a mark-as-paid.
  // Switching an existing invoice to "No Package" has no charge to save, so just close the modal.
  const handleEditInvoice = ({ markAsPaid, sendEmail, ...updateParams }) => {
    if (!hasChargeLineItem(updateParams.lineItems)) {
      setActiveModal(null)
      return
    }

    runInvoiceAction(
      () => requestUpdateInvoice(invoiceId, updateParams).then(() => (
        markAsPaid
          ? requestMarkInvoiceAsPaid(invoiceId, {
            paymentMethod: updateParams.paymentMethod,
            offlinePaymentDescription: updateParams.offlinePaymentDescription,
            sendEmail,
          })
          : null
      )),
      t('enrollmentsBilling.actions.invoiceUpdated')
    )
  }

  return {
    invoiceHandlers,
    handleMarkPaid,
    handleRefund,
    handleEditInvoice,
    handleCreateInvoice,
    handleMarkOutstanding,
    showMarkPaidSuccess,
    setShowMarkPaidSuccess,
    markPaidEmailSent,
    showReminderSuccess,
    setShowReminderSuccess,
  }
}

export default useInvoiceActions
